import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavStep } from '../../lib/NavHistory';
import {
  UserPlus, Stethoscope, DollarSign, Hourglass, CalendarClock, Award,
  Play, Download, Save, ChevronLeft, FileBarChart, Filter, Layers, Megaphone, TrendingUp,
} from 'lucide-react';
import {
  SUBJECTS, BUNDLES, TEMPLATES, DATE_PRESETS, TEMPLATE_SUBJECTS,
  compileEasyReport, templateToConfig, presetRange,
} from '../../lib/easyReports';
import FunnelReport from './FunnelReport';
import PipelineSnapshot from './PipelineSnapshot';
import RepPerformance from './RepPerformance';
import LeadSourceReport from './LeadSourceReport';
import ConversionReport from './ConversionReport';

/*
 * Easy Reports — the Carlos report builder (2026-07-13 requirements doc).
 * Two entry points: template gallery (primary) and build-your-own. The user
 * never sees a table or column name; all mapping lives in
 * src/lib/easyReports.js. Queries run through the existing reports:query /
 * reports:export:csv handlers (inheriting the #182 per-patient PHI audit).
 * "Grouping" is client-side presentation over row listings — deliberately
 * not SQL GROUP BY, so per-patient audit is preserved.
 */

const TEMPLATE_ICONS = {
  UserPlus, Stethoscope, DollarSign, Hourglass, CalendarClock, Award, Filter, Layers, Megaphone, TrendingUp,
};

// #72 slice 1: RBAC gate per openable record type. A name is a live link ONLY
// if the viewer holds the gate for that record type; otherwise it's plain text.
const RECORD_GATE = { contact: 'crm:read', partner: 'alliance_partner:read' };

// #72 Design A (Back + restore): module-scoped, IN-MEMORY only cache of the
// last run. Set when opening a record (so route-level Back to the reports page
// can restore the results view) and CONSUMED on the next mount. NEVER persisted
// (rows are PHI-adjacent) and discarded if it belongs to a different user
// (cross-session guard). Not React state — survives EasyReports' unmount during
// the navigation to the record page.
let _returnCache = null;

const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #DEE2E6', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#495057' };
const btnPrimary = { ...btn, background: '#2E8B98', borderColor: '#2E8B98', color: '#fff', fontWeight: 600 };
// #72: name-cell link (button styled as a link — keyboard-accessible).
const recordLinkStyle = { background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: '#2E8B98', textDecoration: 'underline', cursor: 'pointer' };
const chip = (active) => ({ ...btn, padding: '6px 12px', borderRadius: 16, fontSize: 12, background: active ? '#E7F4F6' : '#fff', borderColor: active ? '#2E8B98' : '#DEE2E6', color: active ? '#2E8B98' : '#495057' });

function fmtCell(value, render, lookups) {
  if (value == null || value === '') return '—';
  switch (render) {
    case 'user': return lookups.users.get(Number(value)) || `User #${value}`;
    case 'org': return lookups.orgs.get(Number(value)) || `Partner #${value}`;
    case 'money_cents': return `$${(Number(value) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    case 'money': return `$${Number(value).toLocaleString('en-US')}`;
    case 'date': return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    default: return String(value);
  }
}

export default function EasyReports({ onSwitchToAdvanced, user, onNavigate }) {
  const [mode, setMode] = useState('gallery');           // gallery | custom | results
  const [cfg, setCfg] = useState(null);                  // active easy config
  const [activeName, setActiveName] = useState('');
  const [rows, setRows] = useState(null);
  const [display, setDisplay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedEasy, setSavedEasy] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [showSave, setShowSave] = useState(false);
  const [lookups, setLookups] = useState({ users: new Map(), orgs: new Map() });

  // #72: viewer permissions (for link-vs-plain-text gating) + current user id.
  const userId = user?.id ?? null;
  const permissions = useMemo(() => new Set(Array.isArray(user?.permissions) ? user.permissions : []), [user]);
  const canOpen = useCallback((recordType) => {
    const gate = RECORD_GATE[recordType];
    return !!gate && permissions.has(gate);
  }, [permissions]);

  // Open a record file. Reuses the EXISTING audited open paths (no client
  // shortcut): CRM contact via the cowork.crm.openContact hand-off (audited by
  // crm:contacts:full-detail -> logPhiAccess); partner via cowork.ap.openOrg.
  // Stashes the current run so route-level Back restores the results.
  const openRecord = useCallback((recordType, id) => {
    if (id == null || !canOpen(recordType) || typeof onNavigate !== 'function') return;
    _returnCache = { userId, config: cfg, rows, display, activeName };
    try {
      if (recordType === 'contact') { sessionStorage.setItem('cowork.crm.openContact', String(id)); onNavigate('crm'); }
      else if (recordType === 'partner') { sessionStorage.setItem('cowork.ap.openOrg', String(id)); onNavigate('alliancepartners'); }
    } catch { /* navigation is best-effort */ }
  }, [canOpen, onNavigate, userId, cfg, rows, display, activeName]);

  // Restore-on-return (Design A): consume the in-memory cache on mount. Only
  // restore if it belongs to THIS user (cross-session PHI guard); otherwise
  // discard. Runs once at mount — the return trip from a record page.
  useEffect(() => {
    if (_returnCache) {
      if (_returnCache.userId === userId && _returnCache.rows) {
        setRows(_returnCache.rows);
        setDisplay(_returnCache.display);
        setCfg(_returnCache.config);
        setActiveName(_returnCache.activeName || '');
        setMode('results');
      }
      _returnCache = null;   // consume (or discard on user mismatch)
    }
    // Intentionally mount-only: this is the return trip from a record page.
  }, []);

  // Resolve friendly names for user/org id columns (business role holds
  // users:read; partner orgs come through the same whitelisted report engine).
  useEffect(() => {
    (async () => {
      const users = new Map(), orgs = new Map();
      try {
        const us = await window.zignagenixAPI.getUsers?.();
        (Array.isArray(us) ? us : us?.users || []).forEach((u) => users.set(Number(u.id), u.display_name || u.username));
      } catch { /* names fall back to #id */ }
      try {
        const r = await window.zignagenixAPI.runReport?.({ table: 'alliance_partner_organizations', columns: ['id', 'name'], limit: 1000 });
        (r?.rows || []).forEach((o) => orgs.set(Number(o.id), o.name));
      } catch { /* names fall back to #id */ }
      setLookups({ users, orgs });
    })();
    loadSaved();
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const reports = await window.zignagenixAPI.getSavedReports?.();
      setSavedEasy((reports || []).filter((r) => r.config?.kind === 'easy_v1'));
    } catch { /* non-fatal */ }
  }, []);

  const run = useCallback(async (config, name) => {
    setLoading(true); setMessage(null);
    try {
      const { querySpec, display: disp } = compileEasyReport(config);
      const res = await window.zignagenixAPI.runReport(querySpec);
      if (res?.error) throw new Error(res.message || 'Report failed');
      setRows(res.rows || []); setDisplay(disp);
      setCfg(config); setActiveName(name);
      setMode('results');
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally { setLoading(false); }
  }, []);

  // ── Funnel (cohort) report — P1b. Its own aggregate handler + display, not
  // the row-listing engine. State + callback are top-level (Rules of Hooks). ──
  const [reportCfg, setReportCfg] = useState(null);   // { datePreset, customFrom, customTo } while picking a range
  const [reportData, setReportData] = useState(null); // last funnel result

  // #65 Phase C2a: the five template report VIEWS (funnel/pipeline/reps/
  // sources/conversion — set via setMode(fcfg.report)) are the master->detail
  // sub-views of this page. Register ONE Back step keyed to that predicate so
  // the global Back (button / keyboard / Android hardware) returns to the
  // gallery (this page's list state) before navigating away — same handler the
  // in-view onBack buttons already use, which stay. Unconditional hook, above
  // every early return (rules-of-hooks). Label is a LITERAL generic string —
  // never a report name, subject, date, or id (no PHI in the nav stack, which
  // persists to sessionStorage). The custom-report 'results'/'custom' modes
  // have a different back target and are deferred to C2b.
  const isReportResultView = ['funnel', 'pipeline', 'reps', 'sources', 'conversion'].includes(mode);
  useNavStep({
    active: isReportResultView,
    label: 'Report results',
    onBack: () => { setMode('gallery'); setReportData(null); },
  });

  // #65 Phase C2b-1: the custom-report sub-views deferred from C2a. Two more Back
  // steps, each with its OWN predicate + the in-view Back target (NOT collapsed
  // into the C2a predicate — they land differently). Unconditional, top-level.
  // LITERAL labels — no report name/subject/id (sessionStorage-backed nav stack).
  //  - 'custom' (report builder): Back → gallery, same as its "Back to reports".
  useNavStep({
    active: mode === 'custom',
    label: 'Report builder',
    onBack: () => { setCfg(null); setMode('gallery'); },
  });
  //  - 'results' (custom-report results): Back → gallery if run from a template
  //    (cfg.templateId set), else → the 'custom' builder — the exact in-view
  //    target `setMode(cfg?.templateId ? 'gallery' : 'custom')`, honoured as-is.
  //    Label deliberately DISTINCT from the C2a 'Report results' above: the two
  //    are mutually exclusive by mode today, but a distinct literal keeps the
  //    stack unambiguous if a future mode ever satisfies both predicates.
  useNavStep({
    active: mode === 'results',
    label: 'Custom report results',
    onBack: () => setMode(cfg?.templateId ? 'gallery' : 'custom'),
  });

  // Dispatch a `report`-flagged template (funnel cohort / pipeline snapshot) to
  // its dedicated aggregate handler. Funnel needs a start date (cohort); the
  // pipeline snapshot defaults to all-time ("right now").
  const runReportKind = useCallback(async (fcfg) => {
    setLoading(true); setMessage(null);
    try {
      let from = null, to = null;
      if (fcfg.customFrom || fcfg.customTo) {
        from = fcfg.customFrom ? new Date(fcfg.customFrom) : null;
        to = fcfg.customTo ? new Date(fcfg.customTo) : null;
      } else if (fcfg.datePreset) {
        const r = presetRange(fcfg.datePreset);
        from = r.from; to = r.to;
      }
      const range = { from: from ? from.toISOString() : null, to: to ? to.toISOString() : null };
      const NAMES = { pipeline: 'Pipeline snapshot', reps: 'Sales rep performance', sources: 'Lead sources', conversion: 'Funnel conversion', funnel: 'Funnel (cohort)' };
      let res;
      if (fcfg.report === 'pipeline') {
        res = await window.zignagenixAPI.runPipelineSnapshot(range); // all-time OK
      } else if (fcfg.report === 'reps') {
        if (!range.from) throw new Error('Choose a date range with a start — the rep cohort is defined by when leads were created.');
        res = await window.zignagenixAPI.runRepPerformance(range);
      } else if (fcfg.report === 'sources') {
        if (!range.from) throw new Error('Choose a date range with a start — the source cohort is defined by when leads were created.');
        res = await window.zignagenixAPI.runLeadSourceReport(range);
      } else if (fcfg.report === 'conversion') {
        if (!range.from) throw new Error('Choose a date range with a start — the conversion cohort is defined by when leads were created.');
        // `compare` (period-comparison toggle) rides on the report config.
        res = await window.zignagenixAPI.runConversionReport({ ...range, compare: !!fcfg.compare });
      } else {
        if (!range.from) throw new Error('Choose a date range with a start — the funnel cohort is defined by when leads were created.');
        res = await window.zignagenixAPI.runFunnelReport(range);
      }
      if (res?.error) throw new Error(res.message || 'Report failed');
      setReportData(res);
      setActiveName(NAMES[fcfg.report] || 'Report');
      setMode(fcfg.report); // 'funnel' | 'pipeline' | 'reps'
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, []);

  const exportCsv = useCallback(async () => {
    if (!cfg) return;
    setLoading(true);
    try {
      const { querySpec } = compileEasyReport(cfg);
      const res = await window.zignagenixAPI.exportReportCsv(querySpec);
      if (res?.error) throw new Error(res.message || 'Export failed');
      const blob = new Blob([res.csv], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(activeName || 'report').replace(/\W+/g, '_').toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
    finally { setLoading(false); }
  }, [cfg, activeName]);

  const saveCurrent = useCallback(async () => {
    if (!cfg || !saveName.trim()) return;
    try {
      await window.zignagenixAPI.createSavedReport?.({ name: saveName.trim(), description: `Easy report (${activeName})`, config: cfg });
      setShowSave(false); setSaveName('');
      setMessage({ type: 'success', text: 'Report saved — it will rerun with this date range.' });
      loadSaved();
    } catch (e) { setMessage({ type: 'error', text: e.message }); }
  }, [cfg, saveName, activeName, loadSaved]);

  // ── Date-range control (shared by every path — the "on the fly" control) ──
  function DateControl({ config, onChange }) {
    const isStale = !!config.staleDays && !config.customFrom && !config.customTo;
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6C757D' }}>Date range:</span>
        {isStale ? (
          <span style={{ fontSize: 12, color: '#F26522', fontWeight: 600 }}>No activity in {config.staleDays}+ days</span>
        ) : (
          <select value={config.datePreset || ''} disabled={!!(config.customFrom || config.customTo)}
            onChange={(e) => onChange({ ...config, datePreset: e.target.value || null })}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #DEE2E6', fontSize: 12 }}>
            <option value="">All time</option>
            <optgroup label="Calendar">
              {DATE_PRESETS.filter((p) => p.kind === 'calendar').map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </optgroup>
            <optgroup label="Rolling">
              {DATE_PRESETS.filter((p) => p.kind === 'rolling').map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </optgroup>
            <optgroup label="Ahead">
              {DATE_PRESETS.filter((p) => p.kind === 'future').map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </optgroup>
          </select>
        )}
        <span style={{ fontSize: 11, color: '#ADB5BD' }}>or custom:</span>
        <input type="date" value={config.customFrom || ''} onChange={(e) => onChange({ ...config, customFrom: e.target.value || null })}
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #DEE2E6', fontSize: 12 }} />
        <span style={{ fontSize: 11, color: '#ADB5BD' }}>to</span>
        <input type="date" value={config.customTo || ''} onChange={(e) => onChange({ ...config, customTo: e.target.value || null })}
          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #DEE2E6', fontSize: 12 }} />
      </div>
    );
  }

  // ── Gallery ────────────────────────────────────────────────────────────────
  const [pendingTemplate, setPendingTemplate] = useState(null); // template config awaiting date tweak

  // Results-view derivations, hoisted here so EVERY render (gallery/custom/
  // results) runs the identical set of hooks — a hook below the early returns
  // below changes the hook count between renders and crashes React (#310, #64).
  // Body is null-safe: returns null until a report has been run.
  const groups = useMemo(() => {
    if (!rows || !display) return null;
    if (!display.clientGroupBy) return [{ key: null, rows }];
    const by = new Map();
    for (const r of rows) {
      const k = r[display.clientGroupBy] ?? '—';
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(r);
    }
    return [...by.entries()].map(([key, groupRows]) => ({ key, rows: groupRows }));
  }, [rows, display]);
  const groupField = display?.fields.find((f) => f.column === display.clientGroupBy);

  if (mode === 'gallery') {
    return (
      <div>
        {message && <Msg m={message} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          {TEMPLATES
            // P1d: management-only templates are hidden unless the viewer holds
            // the required permission (no silent 403 — the card simply isn't shown).
            .filter((t) => !t.requiresPermission || permissions.has(t.requiresPermission))
            .map((t) => {
            const Icon = TEMPLATE_ICONS[t.icon] || FileBarChart;
            const selected = pendingTemplate?.templateId === t.id || (!!t.report && reportCfg?.report === t.report);
            const pick = () => {
              if (t.report) { setPendingTemplate(null); setReportCfg({ report: t.report, datePreset: t.datePreset || null, customFrom: null, customTo: null }); }
              else { setReportCfg(null); setPendingTemplate(templateToConfig(t)); }
            };
            return (
              <button key={t.id} data-testid={`template-${t.id}`}
                onClick={pick}
                style={{ ...card, textAlign: 'left', cursor: 'pointer', border: selected ? '2px solid #2E8B98' : '1px solid #E9ECEF' }}>
                <Icon size={22} color="#2E8B98" />
                <div style={{ fontWeight: 700, fontSize: 14, color: '#212529', marginTop: 8 }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#6C757D', marginTop: 4 }}>{t.blurb}</div>
              </button>
            );
          })}
        </div>

        {pendingTemplate && (
          <div style={{ ...card, marginBottom: 16 }}>
            <DateControl config={pendingTemplate} onChange={setPendingTemplate} />
            <button style={{ ...btnPrimary, marginTop: 12 }} disabled={loading} data-testid="run-template"
              onClick={() => run(pendingTemplate, TEMPLATES.find((t) => t.id === pendingTemplate.templateId)?.name)}>
              <Play size={14} /> Run report
            </button>
          </div>
        )}

        {reportCfg && (
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#6C757D', marginBottom: 8 }}>
              {reportCfg.report === 'pipeline'
                ? 'Snapshot of every active lead right now. Optional: filter to leads created in a date range (default: all time).'
                : reportCfg.report === 'reps'
                ? 'Per-rep performance for the cohort of leads created in this range. Progression is tracked from the Stage History deploy onward.'
                : reportCfg.report === 'sources'
                ? 'Per-source performance for the cohort of leads created in this range, by referral source. Progression is tracked from the Stage History deploy onward.'
                : reportCfg.report === 'conversion'
                ? 'Cohort conversion for leads created in this range: stage-to-stage drop-off, time-in-stage & velocity, and conversion by source. Durations use only post-deploy (non-backfill) stage moves. Toggle "Compare to prior period" to see the equal-length window before this one side-by-side.'
                : 'Cohort = leads created in this range. Progression is tracked from the Stage History deploy onward.'}
            </div>
            <DateControl config={reportCfg} onChange={setReportCfg} />
            {reportCfg.report === 'conversion' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, fontSize: 12, color: '#495057', cursor: 'pointer' }}>
                <input type="checkbox" data-testid="conversion-compare" checked={!!reportCfg.compare}
                  onChange={(e) => setReportCfg({ ...reportCfg, compare: e.target.checked })} />
                Compare to prior period (equal-length window before this one)
              </label>
            )}
            <button style={{ ...btnPrimary, marginTop: 12 }} disabled={loading}
              data-testid={reportCfg.report === 'pipeline' ? 'run-pipeline' : reportCfg.report === 'reps' ? 'run-reps' : reportCfg.report === 'sources' ? 'run-sources' : reportCfg.report === 'conversion' ? 'run-conversion' : 'run-funnel'}
              onClick={() => runReportKind(reportCfg)}>
              <Play size={14} /> {reportCfg.report === 'pipeline' ? 'Run snapshot' : reportCfg.report === 'funnel' ? 'Run funnel' : 'Run report'}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button style={btn} onClick={() => { setCfg(null); setMode('custom'); }}>Or build your own →</button>
          {savedEasy.length > 0 && (
            <select onChange={(e) => { const r = savedEasy.find((x) => String(x.id) === e.target.value); if (r) run(r.config, r.name); }}
              defaultValue="" style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #DEE2E6', fontSize: 13 }}>
              <option value="" disabled>My saved reports…</option>
              {savedEasy.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
          {onSwitchToAdvanced && <button style={{ ...btn, marginLeft: 'auto', fontSize: 12 }} onClick={onSwitchToAdvanced}>Advanced builder</button>}
        </div>
      </div>
    );
  }

  // ── Build your own ────────────────────────────────────────────────────────
  if (mode === 'custom') {
    const c = cfg || { kind: 'easy_v1', subject: 'people', bundles: [...SUBJECTS.people.defaultBundles], datePreset: 'last_30', customFrom: null, customTo: null };
    const subj = SUBJECTS[c.subject];
    const bundles = BUNDLES[subj.table] || [];
    const set = (patch) => setCfg({ ...c, ...patch });
    return (
      <div style={card}>
        {message && <Msg m={message} />}
        <button style={{ ...btn, marginBottom: 14 }} onClick={() => { setCfg(null); setMode('gallery'); }}><ChevronLeft size={14} /> Back to reports</button>

        <div style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>What do you want to report on?</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {Object.entries(SUBJECTS).map(([key, sdef]) => (
            <button key={key} style={chip(c.subject === key)}
              onClick={() => set({ subject: key, bundles: [...sdef.defaultBundles] })}>
              {sdef.label}
            </button>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: '#6C757D', marginBottom: 6 }}>Include which details?</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {bundles.map((b) => (
            <button key={b.key} style={chip(c.bundles.includes(b.key))}
              onClick={() => set({ bundles: c.bundles.includes(b.key) ? c.bundles.filter((k) => k !== b.key) : [...c.bundles, b.key] })}>
              {b.label}
            </button>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}><DateControl config={c} onChange={setCfg} /></div>

        <button style={btnPrimary} disabled={loading || !c.bundles.length} onClick={() => run(c, subj.label)}>
          <Play size={14} /> Run report
        </button>
      </div>
    );
  }

  // ── Funnel (cohort) report — its own aggregate display (P1b) ─────────────
  if (mode === 'funnel') {
    return (
      <div>
        {message && <Msg m={message} />}
        <FunnelReport data={reportData} onBack={() => { setMode('gallery'); setReportData(null); }} />
      </div>
    );
  }

  // ── Pipeline snapshot — point-in-time distribution (P1c) ─────────────────
  if (mode === 'pipeline') {
    return (
      <div>
        {message && <Msg m={message} />}
        <PipelineSnapshot data={reportData} onBack={() => { setMode('gallery'); setReportData(null); }} />
      </div>
    );
  }

  // ── Sales rep performance — management-only per-rep scorecard (P1d) ───────
  if (mode === 'reps') {
    return (
      <div>
        {message && <Msg m={message} />}
        <RepPerformance data={reportData} onBack={() => { setMode('gallery'); setReportData(null); }} />
      </div>
    );
  }

  // ── Lead sources — per-source marketing scorecard (§4.5 Phase 2) ──────────
  if (mode === 'sources') {
    return (
      <div>
        {message && <Msg m={message} />}
        <LeadSourceReport data={reportData} onBack={() => { setMode('gallery'); setReportData(null); }} />
      </div>
    );
  }

  // ── Funnel conversion — org-level cohort conversion analytics (§4.4) ───────
  if (mode === 'conversion') {
    return (
      <div>
        {message && <Msg m={message} />}
        <ConversionReport data={reportData} onBack={() => { setMode('gallery'); setReportData(null); }} />
      </div>
    );
  }

  // ── Results (grouped listing — client-side presentation) ─────────────────
  // NOTE: `groups`/`groupField` are computed above with the other top-level
  // hooks (Rules of Hooks — must run on every render, not below the
  // gallery/custom early returns). See fix #64 (React #310).
  return (
    <div>
      {message && <Msg m={message} />}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btn} onClick={() => setMode(cfg?.templateId ? 'gallery' : 'custom')}><ChevronLeft size={14} /> Back</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>{activeName}</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>{rows?.length ?? 0} rows</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={btn} onClick={() => setShowSave(true)}><Save size={13} /> Save</button>
          <button style={btn} onClick={exportCsv} disabled={loading}><Download size={13} /> Export CSV</button>
        </div>
      </div>
      {cfg && (
        <div style={{ ...card, marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateControl config={cfg} onChange={setCfg} />
          <button style={btnPrimary} disabled={loading} onClick={() => run(cfg, activeName)}><Play size={13} /> Re-run</button>
        </div>
      )}
      {showSave && (
        <div style={{ ...card, marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input autoFocus placeholder="Report name" value={saveName} onChange={(e) => setSaveName(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #DEE2E6', fontSize: 13, flex: 1 }} />
          <button style={btnPrimary} onClick={saveCurrent} disabled={!saveName.trim()}>Save</button>
          <button style={btn} onClick={() => setShowSave(false)}>Cancel</button>
        </div>
      )}
      <div className="responsive-scroll" style={{ ...card, overflow: 'auto', padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 640 }}>
          <thead>
            <tr style={{ background: '#F0F4F8' }}>
              {display?.fields.map((f) => (
                <th key={f.column} style={{ textAlign: 'left', padding: '8px 12px', color: '#495057', fontSize: 12 }}>{f.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups?.map((g) => (
              <React.Fragment key={String(g.key)}>
                {g.key !== null && (
                  <tr style={{ background: '#F8F9FA' }}>
                    <td colSpan={display.fields.length} style={{ padding: '6px 12px', fontWeight: 700, color: '#2E8B98', fontSize: 12 }}>
                      {/* #72: the group HEADER links only when the group key is
                          itself the openable record (e.g. partner in
                          commission_by_partner: grouped by organization_id =
                          idField). Aggregate/non-record group keys stay plain. */}
                      {display.recordType && display.clientGroupBy === display.idField
                        && canOpen(display.recordType) && g.key != null && g.key !== '—'
                        ? <button type="button" className="report-record-link" style={recordLinkStyle}
                            onClick={() => openRecord(display.recordType, g.key)}
                            title="Open record">{fmtCell(g.key, groupField?.render, lookups)}</button>
                        : fmtCell(g.key, groupField?.render, lookups)} · {g.rows.length}
                      {display.groupSubtotal && (
                        <span style={{ marginLeft: 8, color: '#4FA038' }}>
                          {fmtCell(g.rows.reduce((s, r) => s + (Number(r[display.groupSubtotal]) || 0), 0), 'money_cents', lookups)}
                        </span>
                      )}
                    </td>
                  </tr>
                )}
                {g.rows.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #F1F3F5' }}>
                    {display.fields.map((f) => {
                      const cell = fmtCell(r[f.column], f.render, lookups);
                      // #72: linkify the name cell(s) only on LEAF rows, only for
                      // an openable subject the viewer is permitted to open, and
                      // only when the row carries the record id. Else plain text.
                      const linkable = display.recordType
                        && (display.linkFields || []).includes(f.column)
                        && canOpen(display.recordType)
                        && r[display.idField] != null;
                      return (
                        <td key={f.column} style={{ padding: '7px 12px', color: '#212529' }}>
                          {linkable
                            ? <button type="button" className="report-record-link" style={recordLinkStyle}
                                onClick={() => openRecord(display.recordType, r[display.idField])}
                                title="Open record">{cell}</button>
                            : cell}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {(!rows || rows.length === 0) && (
              <tr><td colSpan={display?.fields.length || 1} style={{ padding: 20, textAlign: 'center', color: '#ADB5BD' }}>No rows for this date range.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Msg({ m }) {
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: 13,
      background: m.type === 'error' ? '#FDECEA' : '#EAF7EE', color: m.type === 'error' ? '#B02A37' : '#2E7D32' }}>
      {m.text}
    </div>
  );
}

// Test-only: reset the module-scoped return cache so specs don't leak the
// last run into each other. Not used by the app.
export function __clearReturnCache() { _returnCache = null; }
